import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'hooks';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    Card,
    Container,
    IconButton,
    Text
} from 'mol-ui';
import {
    CardWrapper,
    IconContainer
} from './styled';
import React, { useEffect, useRef, useState } from 'react';

const ChatbotNode = ({
    currentDragging,
    currentZoom,
    data,
    highlight,
    onEdit,
    onMouseUp,
    setCurrentDragging,
    setHeight,
    setPosition
}) => {

    const { featureConfigurationProcessing } = useSelector('config');
    const { disabledColor, successColor } = useTheme();
    const { t } = useTranslation();
    const [dragging, setDragging] = useState(false);
    const [origin, setOrigin] = useState({ x: 0, y: 0 });

    const nodeRef = useRef();

    const {
        content,
        id,
        nodes,
        position_x,
        position_y,
        title,
        type,
        value,
        step
    } = data ?? {};
    const draggingCurrentNode = currentDragging === id;
    const draggingOtherNode = currentDragging !== null && !draggingCurrentNode;

    useEffect(() => {
        if (nodeRef.current) {
            setHeight(nodeRef.current.offsetHeight);
        }
    }, [data, nodeRef.current]);

    const handleMouseDown = e => {
        setDragging(true);
        setCurrentDragging(id);
        setOrigin({
            x: (e.clientX - position_x * currentZoom),
            y: (e.clientY - position_y * currentZoom)
        });
        e.stopPropagation();
    };

    const handleMouseMove = e => {
        if (!dragging || draggingOtherNode) return;
        const newX = (e.clientX - origin.x) / currentZoom;
        const newY = (e.clientY - origin.y) / currentZoom;
        setPosition(
            parseFloat(newX.toFixed(2)),
            parseFloat(newY.toFixed(2))
        );
        e.stopPropagation();
    };

    const handleMouseUp = () => {
        setDragging(false);
        if (draggingCurrentNode) {
            onMouseUp();
            setCurrentDragging(null);
        }
    };

    const parsedColor = draggingOtherNode ? disabledColor : highlight ? successColor : null;

    return (
        <CardWrapper
            cursor={ dragging ? 'grabbing' : 'grab' }
            id={ `chatbot-node-${ data?.id }` }
            left={ `${ position_x }px` }
            ref={ nodeRef }
            top={ `${ position_y }px` }
            zIndex={ dragging && draggingCurrentNode ? 1000 : 1 }
            onMouseDown={ handleMouseDown }
            onMouseLeave={ handleMouseUp }
            onMouseMove={ handleMouseMove }
            onMouseUp={ handleMouseUp }
        >
            <Card color={ parsedColor } margin="0px" width="200px">
                <IconContainer
                    onMouseDown={ e => e.stopPropagation() }
                    onMouseLeave={ e => e.stopPropagation() }
                    onMouseMove={ e => e.stopPropagation() }
                    onMouseUp={ e => e.stopPropagation() }
                >
                    <IconButton disabled={ draggingOtherNode || featureConfigurationProcessing } icon="edit" size={ 14 } onClick={ onEdit } />
                </IconContainer>
                <Container overflowX="hidden">
                    <Text bold color={ parsedColor } margin="0px 0px 5px 0px" textAlign="center">{ id.toString() }</Text>
                    <Text bold color={ parsedColor } >{ title }</Text>
                    { !!content && <Text color={ parsedColor } >{ content }</Text> }
                    <Text bold color={ parsedColor } >{ type }</Text>
                    { step !== undefined && step !== null && <Text color={ parsedColor } textDecoration="underline">{`${ t('Step') }: ${ step }`}</Text>}
                    { value !== undefined && value !== null && <Text color={ parsedColor } >{`${ t('Value') }: ${ value }`}</Text>}
                    { !isEmpty(nodes) && <Text color={ parsedColor } fontSize="11px" margin="5px 0px 0px 0px">{`${ t('Hijos') }: ${ nodes.map(({ id }) => id).join(',') }`}</Text> }
                </Container>
            </Card>
        </CardWrapper>
    );
};

ChatbotNode.propTypes = {
    currentDragging: PropTypes.number,
    currentZoom: PropTypes.number,
    data: PropTypes.shape({
        content: PropTypes.string,
        id: PropTypes.number,
        title: PropTypes.string
    }),
    highlight: PropTypes.bool,
    onEdit: PropTypes.func,
    onMouseUp: PropTypes.func,
    setCurrentDragging: PropTypes.func,
    setHeight: PropTypes.func,
    setPosition: PropTypes.func
};

export default ChatbotNode;
